import type { ActorEvent } from '@lighthouse/core'
import { BLOCK_SIZE, type RecordLikeProtocol } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { getBackgroundStyle, getTextColorStyle, TEXT_FONT_NORMAL, useAppThemeContext } from '@lighthouse/shared'
import React, { forwardRef, memo, useMemo } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { MatrixCell } from './MatrixCell'
import type { MatrixData } from './useMultiDimensionalMatrix'

interface MatrixViewProps {
    node: FlowLayoutNode
    matrixData: MatrixData
    gap: number
    id?: string
    readonly?: boolean
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

const MatrixContainer = styled.div``

/** Grid 布局主体 */
const MatrixGrid = styled.div<{ $gap: number; $colCount: number; $itemWidth: number }>`
    width: max-content;
    display: grid;
    grid-template-columns: max-content repeat(${({ $colCount }) => $colCount}, ${({ $itemWidth }) => $itemWidth}px);
    gap: ${({ $gap }) => $gap}px;
`

/** 左上角单元格 - 同时吸附顶部和左侧 */
const CornerCell = styled.div`
    position: sticky;
    top: 0;
    left: 0;
    z-index: 10;
`

/** 列表头单元格 - 顶部吸附 */
const ColumnHeader = styled.div`
    font-size: 14px;
    color: var(--color-gray-900, #1a1a1a);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: sticky;
    top: 0;
    z-index: 9;
`

/** 行表头单元格 - 左侧吸附 */
const RowHeader = styled.div`
    font-size: 14px;
    color: var(--color-gray-800, #333);
    white-space: nowrap;
    position: sticky;
    left: 0;
    z-index: 8;
    &::before {
        content: '';
        display: inline-block;
        height: 100%;
        vertical-align: middle;
    }
`

export const MatrixView = memo(
    forwardRef<HTMLDivElement, MatrixViewProps>(({ node, matrixData, gap, id, readonly, onRecordClick }, ref) => {
        const { blockData } = useViewBlockContext()
        const { id: blockId, title, config } = blockData
        const { breakPoint: baseBreakPointConfig } = config
        const { multiDimensionalStyle, viewLayout } = baseBreakPointConfig
        const { size = BLOCK_SIZE.md } = viewLayout || {}

        const {
            theme: { palettes }
        } = useAppThemeContext()

        const { row: rowStyle, col: colStyle } = multiDimensionalStyle || {}

        // 列表头样式（文字 + 背景）
        const colHeaderStyle = useMemo(() => {
            if (!colStyle) {
                return {}
            }
            const { fontSize, fontFamily, color, background } = colStyle
            const fs = fontSize?.size ? `${fontSize.size}${fontSize.unit || 'px'}` : undefined
            return {
                fontSize: fs,
                fontFamily: fontFamily === TEXT_FONT_NORMAL ? undefined : fontFamily,
                ...getTextColorStyle(color, palettes),
                ...getBackgroundStyle({ value: background, palettes })
            }
        }, [colStyle, palettes])

        // 行表头样式（文字 + 背景）
        const rowHeaderStyle = useMemo(() => {
            if (!rowStyle) {
                return {}
            }
            const { fontSize, fontFamily, color, textAlign, background } = rowStyle
            const fs = fontSize?.size ? `${fontSize.size}${fontSize.unit || 'px'}` : undefined
            return {
                fontSize: fs,
                fontFamily: fontFamily === TEXT_FONT_NORMAL ? undefined : fontFamily,
                textAlign,
                ...getTextColorStyle(color, palettes),
                ...getBackgroundStyle({ value: background, palettes })
            }
        }, [rowStyle, palettes])

        // 列宽度
        const itemWidth = useMemo(() => {
            const SIZE_MAP = {
                [BLOCK_SIZE.sm]: 90,
                [BLOCK_SIZE.md]: 110,
                [BLOCK_SIZE.lg]: 130,
                [BLOCK_SIZE.xl]: 150
            }
            return SIZE_MAP[size]
        }, [size])

        // 行表头高度
        const rowHeaderHeight = useMemo(() => {
            const height = rowStyle?.size?.size
            if (typeof height === 'number') {
                return `${height}px`
            }
            return '40px'
        }, [rowStyle?.size?.size])

        // 列表头高度
        const colHeaderWidth = useMemo(() => {
            const width = colStyle?.size?.size
            if (typeof width === 'number') {
                return `${width}px`
            }
            return '100px'
        }, [colStyle?.size?.size])

        const { rowKeys, colKeys, matrix } = matrixData

        const canEditIndex = useMemo(() => {
            for (const [i, rowKey] of rowKeys.entries()) {
                for (const [j, colKey] of colKeys.entries()) {
                    const cell = matrix.get(rowKey)?.get(colKey)
                    if (cell && cell.records.length > 0) {
                        return i * colKeys.length + j
                    }
                }
            }
            return 0
        }, [rowKeys, colKeys, matrix])

        if (rowKeys.length === 0 || colKeys.length === 0) {
            return null
        }

        return (
            <MatrixContainer ref={ref} id={id}>
                <MatrixGrid $gap={gap} $colCount={colKeys.length} $itemWidth={itemWidth}>
                    {/* 表头行 */}
                    <CornerCell style={{ ...rowHeaderStyle, height: rowHeaderHeight, lineHeight: rowHeaderHeight }} />
                    {colKeys.map(colKey => (
                        <ColumnHeader key={colKey} style={{ ...rowHeaderStyle, height: rowHeaderHeight, lineHeight: rowHeaderHeight }}>
                            {colKey}
                        </ColumnHeader>
                    ))}

                    {/* 数据行 */}
                    {rowKeys.map((rowKey, rowIndex) => (
                        <React.Fragment key={rowKey}>
                            <RowHeader style={{ ...colHeaderStyle, width: colHeaderWidth }}>
                                <span
                                    style={{
                                        verticalAlign:
                                            colStyle?.textAlign === 'center' ? 'middle' : colStyle?.textAlign === 'right' ? 'bottom' : 'top'
                                    }}
                                >
                                    {rowKey}
                                </span>
                            </RowHeader>
                            {colKeys.map((colKey, colIndex) => {
                                const cell = matrix.get(rowKey)?.get(colKey)
                                return (
                                    <MatrixCell
                                        key={`${rowKey}-${colKey}`}
                                        canEdit={canEditIndex === rowIndex * colKeys.length + colIndex}
                                        node={node}
                                        blockId={blockId}
                                        title={title}
                                        records={cell?.records || []}
                                        rowKey={rowKey}
                                        colKey={colKey}
                                        recordIndexPath={`${rowIndex}_${colIndex}`}
                                        readonly={readonly}
                                        onRecordClick={onRecordClick}
                                    />
                                )
                            })}
                        </React.Fragment>
                    ))}
                </MatrixGrid>
            </MatrixContainer>
        )
    })
)
